/**
 * RemoteCastGeometryPlayer — draws ANOTHER player's cast from its geometry
 * (plan 29 F5, lane BV 2026-10-11).
 *
 * The cast broadcast (`abilityCasted`: caster, ability, target) plus the
 * ability's geometry snapshot (castGeometry.gen.ts) is all it needs: the plan
 * is built by the same `castVisualPlan` the caster's behaviours emit through,
 * anchored on the remote body (`playerCombatPoint` of the interpolated remote
 * player) and facing the caster's way. A melee swing at a target turns the
 * way the caster's own verdict turns (meleeSwingVerdict), and only a target
 * inside the cone becomes a lightning swing's contact.
 *
 * Presentation only (remote-visuals rule): the plan holds `vfx:*` events and
 * nothing else — no area hits, no damage — and every spawn is stamped
 * `origin: 'remote'` so screen shake/flash stay on the caster's screen.
 *
 * Cost (bar point 7, several players fighting): at most
 * `maxActiveRemoteCasts` plans draw at once (a cast past the cap is dropped);
 * a cast whose drawn extent is wholly off camera is not drawn. Each plan's
 * shapes are the existing draw-once renderers (transform/alpha animation).
 */
import { REMOTE_VFX_CONFIG } from '../config/remote-vfx.config';
import { REMOTE_VFX_ORIGIN } from '../screenFeelScope';
import { resolveCastGeometry, hasRemoteVisuals, type CastGeometry } from '../castGeometry/castGeometry';
import { castVisualPlan, landsOnArrival, planLifetimeMs, type VisualEmit } from '../castGeometry/castVisualPlan';
import { emitVisualPlan } from '../castGeometry/emitVisualPlan';
import { meleeSwingLook } from '../castGeometry/meleeSwingLook';
import { novaLaunches } from '../castGeometry/novaLaunches';
import { meleeSwingVerdict } from '../../../shared/logic/meleeSwingVerdict';
import { playerCombatPoint } from '../../../shared/logic/CombatAnchors';
import type { ReachBody } from '../../../shared/logic/BodyReach';
import { SequencedCastWindup } from '../../systems/combat/SequencedCastWindup';

export interface CameraViewLike { centerX: number; centerY: number; width: number; height: number }

/** The remote caster as this screen holds it. */
export interface RemoteCaster {
  position: { x: number; y: number };
  width?: number;
  height?: number;
  facing?: 'left' | 'right' | string;
  class?: string;
  health?: number;
  isDead?: boolean;
  isOnline?: boolean;
}

export type RemotePlayOutcome = 'played' | 'no_geometry' | 'culled' | 'capped';

export interface RemoteCastGeometryDeps {
  now?: () => number;
  cameraView?: () => CameraViewLike | null;
  targetBody?: (targetId: string) => ReachBody | null;
  /** Re-read the live row: network hydration may replace the original object. */
  currentCaster?: (casterId: string) => RemoteCaster | null | undefined;
  /** Stable authoritative row, distinct from replaceable interpolation rows. */
  casterIdentity?: (casterId: string) => object | null;
  emit?: (plan: VisualEmit[], timers: Set<ReturnType<typeof setTimeout>>) => void;
  /** Where a nova's visual projectiles are launched (the observer's ProjectileSystem). */
  launch?: (projectile: RemoteNovaProjectile) => void;
}

/** One visual (zero-damage) projectile of another player's nova / fan. */
export interface RemoteNovaProjectile {
  x: number; y: number; ownerId: string; damage: 0; type: 'basic'; direction: 'left' | 'right';
  initialVx: number; initialVy: number; shape: string; color: number; speed: number;
  width: number; height: number; gravity: number; lifespan: number; startTime: number; abilityId: string;
}

/** The farthest any entry of the plan draws from its own point (px). */
function extentOf(payload: Record<string, unknown>): number {
  const n = (k: string) => (typeof payload[k] === 'number' ? (payload[k] as number) : 0);
  const cone = payload.cone as { rangePx?: number } | undefined;
  return Math.max(n('radius'), n('areaRadius'), n('arcRadius'), n('rangePx'), cone?.rangePx ?? 0, 48);
}

/** True when any entry of the plan reaches into the camera view (+ margin). */
export function planTouchesView(plan: readonly VisualEmit[], body: { x: number; y: number }, view: CameraViewLike, marginPx: number): boolean {
  const halfW = view.width / 2 + marginPx;
  const halfH = view.height / 2 + marginPx;
  for (const e of plan) {
    const p = e.payload as unknown as Record<string, unknown>;
    const x = typeof p.x === 'number' ? p.x : body.x;
    const y = typeof p.y === 'number' ? p.y : body.y;
    const r = extentOf(p);
    if (Math.abs(x - view.centerX) <= halfW + r && Math.abs(y - view.centerY) <= halfH + r) return true;
  }
  return false;
}

/** Stamp remote origin on spawns (screenFeelScope keeps shake/flash local). */
function decorateRemote(e: VisualEmit): VisualEmit {
  if (e.event !== 'vfx:spawn') return e;
  return { ...e, payload: { ...(e.payload as object), origin: REMOTE_VFX_ORIGIN } } as VisualEmit;
}

export class RemoteCastGeometryPlayer {
  /** Expiry time of every plan currently holding a budget slot. */
  private slots: Array<{ expiresAt: number }> = [];
  private timers = new Set<ReturnType<typeof setTimeout>>();
  private howlWindups = new SequencedCastWindup();

  constructor(private readonly deps: RemoteCastGeometryDeps = {}) {}

  /** Plans drawing right now (the budget's view). */
  activeCount(): number {
    const now = this.now();
    this.slots = this.slots.filter((slot) => slot.expiresAt > now);
    return this.slots.length;
  }

  /**
   * The plan another player's cast draws on this screen (no budget, no
   * culling). Howl's `play` refreshes its delayed cone pose at completion.
   * Exposed for the stationary parity contract.
   */
  planFor(abilityId: string, casterId: string, caster: RemoteCaster, targetId?: string, geometry?: CastGeometry): VisualEmit[] {
    const geom = geometry ?? resolveCastGeometry(abilityId);
    if (!hasRemoteVisuals(geom)) return [];
    const body = playerCombatPoint(caster);
    let facingRight = caster.facing !== 'left';
    let swingTargetId: string | undefined;
    if (geom.swing && targetId) {
      const cone = meleeSwingLook(abilityId, caster.class, geom.swing.lineColor).cone;
      const verdict = meleeSwingVerdict(body, facingRight ? 'right' : 'left', cone, this.deps.targetBody?.(targetId) ?? null);
      if (verdict.lands) { facingRight = verdict.facing === 'right'; swingTargetId = targetId; }
    }
    const plan = castVisualPlan(geom, { abilityId, casterId, heroClass: caster.class, body, facingRight }, { swingTargetId, now: this.now() });
    const delay = geom.castDelayMs ?? 0;
    if (delay <= 0) return plan;
    // The wind-up tell rides the caster for the wind-up, as on the caster's own
    // screen (the factory's wind-up wrapper); everything else waits for it.
    const tell: VisualEmit[] = geom.castDelayEffectId
      ? [{ event: 'vfx:spawn', delayMs: 0, payload: { effectId: geom.castDelayEffectId, x: body.x, y: body.y, duration: delay, casterId, abilityId } } as VisualEmit]
      : [];
    return [...tell, ...plan.map((e) => ({ ...e, delayMs: e.delayMs + delay }))];
  }

  /** Draw another player's cast, within the budget and the camera. */
  /** A nova's visual projectiles for a remote caster (NovaProjectileBehavior's launches, damage 0). */
  novaFor(abilityId: string, casterId: string, caster: RemoteCaster, geometry?: CastGeometry): RemoteNovaProjectile[] {
    const nova = (geometry ?? resolveCastGeometry(abilityId))?.nova;
    if (!nova) return [];
    const startTime = this.now();
    return novaLaunches(nova, playerCombatPoint(caster), caster.facing !== 'left').map((l) => ({
      x: l.x, y: l.y, ownerId: casterId, damage: 0, type: 'basic', direction: l.isRight ? 'right' : 'left',
      initialVx: l.vx, initialVy: l.vy, shape: nova.shape, color: nova.color, speed: nova.speed,
      width: nova.width, height: nova.height, gravity: nova.gravity, lifespan: nova.lifespan, startTime, abilityId,
    }));
  }

  play(abilityId: string, casterId: string, caster: RemoteCaster, targetId?: string, clientCastSeq?: number, legacyIdentity?: object): RemotePlayOutcome {
    const geometry = resolveCastGeometry(abilityId);
    if (abilityId === 'howl_of_terror' && geometry?.cone && (geometry.castDelayMs ?? 0) > 0) {
      return this.playDelayedHowl(abilityId, casterId, caster, geometry, clientCastSeq, legacyIdentity);
    }
    const plan = this.planFor(abilityId, casterId, caster, targetId);
    const nova = this.deps.launch ? this.novaFor(abilityId, casterId, caster) : [];
    if (plan.length === 0 && nova.length === 0) return 'no_geometry';
    return this.emitWithinBudget(plan, caster, nova);
  }

  /** One budget reservation owns both the immediate tell and the delayed wave. */
  private playDelayedHowl(abilityId: string, casterId: string, caster: RemoteCaster, geometry: CastGeometry, clientCastSeq?: number, legacyIdentity?: object): RemotePlayOutcome {
    const plan = this.planFor(abilityId, casterId, caster, undefined, geometry);
    const outcome = this.reserveBudget(plan, caster);
    if (outcome !== 'played') return outcome;
    const slot = this.slots[this.slots.length - 1];
    const actor = this.deps.casterIdentity?.(casterId);
    const currentCaster = () => this.deps.currentCaster ? this.deps.currentCaster(casterId) : caster;
    const releaseSlot = () => { this.slots = this.slots.filter(entry => entry !== slot); };
    const started = this.howlWindups.start({ abilityId, casterId, clientCastSeq, legacyIdentity,
      duration: geometry.castDelayMs!, onRetire: releaseSlot,
      isCurrent: () => {
        const current = currentCaster();
        return !!current && !current.isDead && current.isOnline !== false && (current.health === undefined || current.health > 0)
          && (!this.deps.casterIdentity || (!!actor && this.deps.casterIdentity(casterId) === actor));
      },
      tell: instanceId => this.emitPlan(plan.filter(entry => entry.delayMs === 0).map(entry => ({
        ...entry, payload: { ...entry.payload, instanceId },
      }) as VisualEmit)),
      complete: () => {
        const current = currentCaster();
        if (!current) return;
        const body = playerCombatPoint(current);
        const completion = castVisualPlan({ cone: geometry.cone }, {
          abilityId, casterId, heroClass: current.class, body, facingRight: current.facing !== 'left',
        });
        const view = this.deps.cameraView?.() ?? null;
        if (!view || planTouchesView(completion, body, view, REMOTE_VFX_CONFIG.remoteCullMarginPx)) this.emitPlan(completion);
      },
    });
    if (!started) releaseSlot();
    return 'played';
  }

  private reserveBudget(plan: VisualEmit[], caster: RemoteCaster, nova: RemoteNovaProjectile[] = []): RemotePlayOutcome {
    const view = this.deps.cameraView?.() ?? null;
    const body = playerCombatPoint(caster);
    // A nova reaches as far as its knives fly.
    const reach = nova.length ? [{ event: 'vfx:spawn', payload: { x: body.x, y: body.y, radius: (nova[0].speed * nova[0].lifespan) / 1000 }, delayMs: 0 } as unknown as VisualEmit] : [];
    if (view && !planTouchesView([...plan, ...reach], body, view, REMOTE_VFX_CONFIG.remoteCullMarginPx)) return 'culled';
    if (this.activeCount() >= REMOTE_VFX_CONFIG.maxActiveRemoteCasts) return 'capped';
    this.slots.push({ expiresAt: this.now() + Math.max(REMOTE_VFX_CONFIG.remoteMinSlotMs, planLifetimeMs(plan), nova[0]?.lifespan ?? 0) });
    return 'played';
  }

  private emitPlan(plan: VisualEmit[]): void {
    const decorated = plan.map(decorateRemote);
    if (this.deps.emit) this.deps.emit(decorated, this.timers);
    else emitVisualPlan(decorated, { timers: this.timers });
  }

  private emitWithinBudget(plan: VisualEmit[], caster: RemoteCaster, nova: RemoteNovaProjectile[] = []): RemotePlayOutcome {
    const outcome = this.reserveBudget(plan, caster, nova);
    if (outcome !== 'played') return outcome;
    this.emitPlan(plan);
    for (const p of nova) this.deps.launch?.(p);
    return 'played';
  }

  /**
   * A dash's arrival (Charge, Shield Charge): the swing it carries, drawn at
   * the landing body (`landing` is the server's top-left, like `position`).
   */
  playArrival(abilityId: string, casterId: string, caster: RemoteCaster, landing: { x: number; y: number }): RemotePlayOutcome {
    const geom = resolveCastGeometry(abilityId);
    // The swing a dash carries, and a travel dash's landing slam (lane BX).
    const area = geom && landsOnArrival(geom) ? geom.area : undefined;
    if (!geom?.swing && !area) return 'no_geometry';
    const landed: RemoteCaster = { ...caster, position: landing };
    const body = playerCombatPoint(landed);
    const plan = castVisualPlan({ swing: geom!.swing, area }, { abilityId, casterId, heroClass: caster.class, body, facingRight: caster.facing !== 'left' });
    return this.emitWithinBudget(plan, landed);
  }

  /** Cancel pending delayed emits (scene teardown). */
  teardown(): void {
    this.howlWindups.clear();
    for (const t of this.timers) clearTimeout(t);
    this.timers.clear();
    this.slots = [];
  }

  private now(): number {
    return this.deps.now ? this.deps.now() : Date.now();
  }
}
